import { loadBaseCSS } from '../shared/button-utils.js';
import { generateControlId, upgradeProperty } from '../shared/form-control.js';
import { deepActiveElement, overlayContains } from '../shared/overlay-utils.js';
import { numberAttribute, primitiveEvent, primitiveStyles } from '../shared/primitive-utils.js';

export interface QdsDisclosureToggleDetail { component: QdsDisclosure; expanded: boolean; value: string; }

/**
 * A persistent, keyboard-accessible disclosure. Its contents stay mounted when closed.
 * @element qds-disclosure
 * @attr {string} heading - Heading text, replaced by the heading slot.
 * @attr {number} heading-level - Heading level from 2 through 6. Default 3.
 * @attr {boolean} expanded - Whether the panel is expanded.
 * @attr {boolean} disabled - Disables user toggling without hiding the content.
 * @attr {boolean} region - Expose the panel as a labelled region. Use sparingly.
 * @attr {string} value - Optional identity within a qds-accordion.
 * @property {boolean} expanded - Expanded state.
 * @property {boolean} disabled - Disabled state.
 * @property {string} value - Item identity.
 * @slot heading - Noninteractive heading content.
 * @slot - Panel content.
 * @csspart heading - Heading container.
 * @csspart trigger - Native toggle button.
 * @csspart panel - Persistent content container.
 * @fires {{ component: HTMLElement; expanded: boolean; value: string }} qds-before-toggle - Cancelable user toggle request. Detail: QdsDisclosureToggleDetail.
 * @fires {{ component: HTMLElement; expanded: boolean; value: string }} qds-toggle - Completed user toggle. Detail: QdsDisclosureToggleDetail.
 */
export class QdsDisclosure extends HTMLElement {
  static get observedAttributes(): string[] { return ['heading', 'heading-level', 'expanded', 'disabled', 'region', 'value']; }
  private _trigger: HTMLButtonElement;
  private _panel: HTMLElement;
  constructor() {
    super();
    const id = generateControlId('disclosure');
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: block; border: 1px solid var(--theme-border-primary, #e4e9f2); border-radius: var(--theme-radius-sm, 4px); }
      [part="heading"] { margin: 0; font: inherit; }
      button { display: flex; align-items: center; gap: 12px; text-align: start; width: 100%; min-height: 44px;
        padding: 12px 16px; border: 0; background: var(--theme-background-secondary, #f6f8fc); cursor: pointer; font-weight: 600; }
      button:hover:not(:disabled) { background: var(--theme-background-hover, rgba(0,0,0,.05)); }
      button:disabled { cursor: default; color: var(--theme-text-secondary, #666); }
      .chevron { margin-inline-start: auto; transform: rotate(0); }
      button[aria-expanded="true"] .chevron { transform: rotate(90deg); }
      [part="panel"] { padding: 16px; }
    </style><div part="heading" role="heading"><button part="trigger" type="button" id="${id}-trigger" aria-controls="${id}-panel">
      <slot name="heading"><span></span></slot><span class="chevron" aria-hidden="true">›</span></button></div>
      <div part="panel" id="${id}-panel" aria-labelledby="${id}-trigger"><slot></slot></div>`;
    this._trigger = this.shadowRoot!.querySelector('button')!;
    this._panel = this.shadowRoot!.querySelector('[part="panel"]')!;
    loadBaseCSS();
  }
  connectedCallback(): void {
    for (const prop of ['expanded', 'disabled', 'value']) upgradeProperty(this, prop);
    this._trigger.addEventListener('click', this._onClick);
    this._sync();
  }
  disconnectedCallback(): void { this._trigger.removeEventListener('click', this._onClick); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get expanded(): boolean { return this.hasAttribute('expanded'); }
  set expanded(value: boolean) { this.toggleAttribute('expanded', !!value); }
  get disabled(): boolean { return this.hasAttribute('disabled'); }
  set disabled(value: boolean) { this.toggleAttribute('disabled', !!value); }
  get value(): string { return this.getAttribute('value') || ''; }
  set value(value: string) { this.setAttribute('value', value ?? ''); }
  /** Move focus to the native header button. */
  focus(options?: FocusOptions): void { this._trigger.focus(options); }
  /** Request a user-style toggle, respecting disabled state and cancellation. */
  toggle(): boolean {
    if (this.disabled) return false;
    const detail = { component: this, expanded: !this.expanded, value: this.value };
    if (!primitiveEvent(this, 'qds-before-toggle', detail, true)) return false;
    this.expanded = detail.expanded;
    primitiveEvent(this, 'qds-toggle', detail);
    return true;
  }
  private _sync(): void {
    if (!this.expanded && overlayContains(this._panel, deepActiveElement(this.ownerDocument))) this.focus();
    this._trigger.disabled = this.disabled;
    this._trigger.setAttribute('aria-expanded', String(this.expanded));
    this.shadowRoot!.querySelector('[part="heading"]')!.setAttribute('aria-level', String(Math.max(2, Math.min(6, Math.trunc(numberAttribute(this, 'heading-level', 3))))));
    this.shadowRoot!.querySelector('slot[name="heading"] span')!.textContent = this.getAttribute('heading') || 'Details';
    this._panel.hidden = !this.expanded;
    if (this.hasAttribute('region')) this._panel.setAttribute('role', 'region');
    else this._panel.removeAttribute('role');
  }
  private _onClick = (): void => { this.toggle(); };
}
if (!customElements.get('qds-disclosure')) customElements.define('qds-disclosure', QdsDisclosure);
